import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link 
            to="/" 
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#F58227] transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <div className="mb-12">
              <p className="text-sm font-medium uppercase tracking-widest text-[#0CBCBF] mb-4">
                Legal
              </p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#27272A] mb-6">
                Terms of Service
              </h1>
              <p className="text-[#A1A1AA]">
                Effective Date: December 2025
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none space-y-8 text-[#52525B]">
              {/* Security Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
                <p className="text-sm text-amber-800 font-medium">
                  Important: For your safety, don't store passwords or sensitive credentials in this app.
                </p>
              </div>

              <p className="text-lg leading-relaxed">
                Welcome to Where was i. By using this application, you agree to the following terms.
              </p>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Use of the Service
                </h2>
                <p className="leading-relaxed">
                  Where was i provides tools to help users organize tasks and regain focus. You agree to use the service only for lawful purposes.
                </p>
                <p className="leading-relaxed mt-4">
                  You may not attempt to disrupt, damage, or gain unauthorized access to the service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Accounts
                </h2>
                <p className="leading-relaxed">
                  If you create an account, you are responsible for maintaining the security of your login credentials.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Availability
                </h2>
                <p className="leading-relaxed">
                  We aim to keep the service available and reliable but do not guarantee uninterrupted operation. The service may occasionally experience downtime for maintenance or updates.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  User Content
                </h2>
                <p className="leading-relaxed">
                  You retain ownership of the tasks and content you create within the app.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Subscriptions
                </h2>
                <p className="leading-relaxed">
                  Some features may require a paid subscription. Pricing and billing details will be presented clearly before purchase.
                </p>
                <p className="leading-relaxed mt-4">
                  Subscriptions may be canceled at any time according to the billing platform used.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Limitation of Liability
                </h2>
                <p className="leading-relaxed">
                  Where was i is provided "as is" without warranties of any kind.
                </p>
                <p className="leading-relaxed mt-4">
                  To the fullest extent permitted by law, we are not liable for damages arising from use of the service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Changes
                </h2>
                <p className="leading-relaxed">
                  These terms may be updated from time to time. Continued use of the service constitutes acceptance of the updated terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Contact
                </h2>
                <p className="leading-relaxed">
                  For questions regarding these terms, contact:
                </p>
                <p className="leading-relaxed mt-2">
                  <a 
                    href="mailto:support@getwherewasi.com" 
                    className="text-[#0CBCBF] hover:text-[#F58227] transition-colors"
                  >
                    support@getwherewasi.com
                  </a>
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-16 text-center">
              <Link
                to="/"
                data-testid="terms-cta-home"
                className="inline-flex items-center gap-2 bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full px-8 py-4 text-lg font-bold shadow-lg hover:shadow-[#F58227]/30 transition-all active:scale-95"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
